'use client';

import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Info } from 'lucide-react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/components/commons/Button/Button';
import OtpInput from '@/components/commons/OtpInput/OtpInput';
import { useAuth } from '@/hooks/useAuth';
import { useLogger } from '@/hooks/useLogger';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { cleanPhoneNumber, isValidPhone } from '@/utils/helpers';
import { Timer } from './components/Timer';

const EMAIL_REGEX = /[@.]|[a-zA-Z]/;
const isEmailInput = (value: string): boolean => {
  // Check if it contains @, ., or any letters.
  return !!value.match(EMAIL_REGEX);
};

export function LoginForm() {
  const logger = useLogger();
  const tApi = useTranslations('ApiErrors.auth');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { login, verifyOtp, error: authError } = useAuth();
  const [formData, setFormData] = useState({
    identifier: '',
    otp: '',
  });
  const [error, setError] = useState('');
  const [isSpecificError, setIsSpecificError] = useState(false);
  const [step, setStep] = useState<'identifier' | 'otp'>('identifier');
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  useEffect(() => {
    logger.info('LoginForm mounted');
  }, [logger]);

  const handleIdentifierSubmit = async () => {
    logger.info('Attempting to submit identifier');
    try {
      // Clean phone number for submission if it's a phone number
      const cleanedIdentifier = isValidPhone(formData.identifier)
        ? cleanPhoneNumber(formData.identifier)
        : formData.identifier;

      const success = await login(cleanedIdentifier);
      if (!success) {
        logger.warn('Identifier submission failed: API returned unsuccessful');
        return setError('Unauthorized phone number or email address');
      }
      logger.info('Identifier submission successful');
      setStep('otp');
      setIsTimerActive(true);
    } catch (err) {
      logger.error('Identifier submission failed with error', { error: err });
      setError('Invalid phone number or email address');
    }
  };

  const handleOtpSubmit = async (otpValue?: string) => {
    const currentOtp = otpValue || formData.otp;
    logger.info('Attempting to submit OTP');

    if (currentOtp.length !== 6) {
      logger.warn('Invalid OTP length');
      setError(tApi('invalidOtp'));
      return;
    }

    // Set loading state to prevent multiple submissions
    setIsOtpLoading(true);
    setError(''); // Clear any previous errors

    try {
      // Clean phone number for submission if it's a phone number
      const cleanedIdentifier = isValidPhone(formData.identifier)
        ? cleanPhoneNumber(formData.identifier)
        : formData.identifier;

      const success = await verifyOtp(cleanedIdentifier, currentOtp);
      if (!success) {
        logger.warn('OTP verification failed: API returned unsuccessful');
        setError(tApi('invalidOtp'));
        return;
      }
      logger.info('OTP verification successful, redirecting');
      const from = searchParams.get('from') || '/dashboard';
      if (from === '/login') {
        router.push('/dashboard');
      } else {
        router.push(from);
      }
    } catch (err) {
      logger.error('OTP submission failed with error', { error: err });
      setError(tApi('invalidOtp'));
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    logger.info('Form submission started', { step });
    setIsLoading(true);
    e.preventDefault();
    setError('');
    setIsSpecificError(false);

    try {
      if (step === 'identifier') {
        await handleIdentifierSubmit();
      }
    } catch (err) {
      logger.error('Form submission failed with error', { step, error: err });
      setError(
        step === 'identifier'
          ? 'Invalid phone number or email address'
          : tApi('invalidOtp')
      );
    } finally {
      setIsLoading(false);
      logger.info('Form submission finished', { step });
    }
  };

  const updateFormData = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIdentifierChange = (value: string) => {
    if (value === '' || isEmailInput(value)) {
      updateFormData('identifier', value);
      return;
    }

    updateFormData('identifier', formatPhoneNumber(value));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'identifier') {
      handleIdentifierChange(value);
    } else {
      updateFormData(name, value);
    }
  };

  const handleOtpInputChange = (otpValue: string) => {
    setFormData((prev) => ({ ...prev, otp: otpValue }));
    setError('');
    if (otpValue.length === 6 && !isOtpLoading) {
      handleOtpSubmit(otpValue);
    }
  };

  const handleResendCode = async () => {
    logger.info('Resend code requested');
    setError('');
    setIsSpecificError(false);
    try {
      setIsLoading(true);
      // Clean phone number for submission if it's a phone number
      const cleanedIdentifier = isValidPhone(formData.identifier)
        ? cleanPhoneNumber(formData.identifier)
        : formData.identifier;

      await login(cleanedIdentifier);
      setIsTimerActive(true);
      setIsLoading(false);
    } catch (err) {
      logger.error('Resend code failed', { error: err });
      if (!isSpecificError) {
        setError(tApi('resendFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <div>
        <Image
          alt="logo"
          className="mx-auto"
          height={100}
          src="/assets/logos/aa-logo-no-text.svg"
          style={{ height: 'auto' }}
          width={100}
        />

        <h2 className="mt-6 text-center font-semibold text-2xl text-foreground">
          {step === 'identifier'
            ? 'Login to Resident Portal'
            : 'Enter login code'}
        </h2>

        {step === 'otp' && (
          <p className="mt-2 text-center font-medium text-muted-foreground text-sm">
            {"Enter the code we've sent you"}
          </p>
        )}
      </div>

      <form className="mx-auto mt-6 max-w-xs space-y-2" onSubmit={handleSubmit}>
        <div className="rounded-md">
          {step === 'identifier' ? (
            <div>
              <label className="sr-only" htmlFor="identifier">
                Phone number or email address
              </label>

              <input
                autoComplete="username"
                className="relative block w-full min-w-[320px] appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 text-sm placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                id="identifier"
                name="identifier"
                onChange={handleChange}
                placeholder={'Enter your phone number or email address'}
                required
                type="text"
                value={formData.identifier}
              />
            </div>
          ) : (
            <OtpInput
              error={!!(error || authError) && !isSpecificError}
              handleOtpSubmit={handleOtpInputChange}
              length={6}
              loading={isOtpLoading}
            />
          )}
        </div>

        <div>
          <Button
            className="relative w-full px-4 py-2"
            disabled={isLoading}
            style={{ display: step === 'otp' ? 'none' : 'block' }}
            type="submit"
            variant="primary"
          >
            {'Continue'}
          </Button>
        </div>

        {error && step === 'identifier' && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-2">
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full">
                <Info className="text-red-600" size={20} />
              </div>

              <p className="font-medium text-red-800 text-sm leading-5">
                {error}
              </p>
            </div>
          </div>
        )}

        {step === 'otp' && (
          <div className="mt-4 text-center">
            {isTimerActive ? (
              <Timer onTimerEnd={() => setIsTimerActive(false)} />
            ) : (
              !isSpecificError && (
                <Button
                  className="font-medium text-pink-600 text-sm hover:text-pink-700"
                  disabled={isLoading || isOtpLoading}
                  onClick={handleResendCode}
                  type="button"
                  variant="link"
                >
                  {'Resend the code'}
                </Button>
              )
            )}
          </div>
        )}

        {isSpecificError && (
          <div className="rounded-md border p-4">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="mt-0.5 h-6 min-w-6 text-destructive" />

              <p className="font-medium text-base text-destructive leading-normal">
                You've unsubscribed from SMS. Text <strong>START</strong> to{' '}
                <strong>(940) 283-7368</strong> to resume.
              </p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
